import { SceneWithUserNames } from '../../types/scene.types';
import { compareSceneCodes, SceneCodeParts } from '../../pages/Breakdown/BreakdownMain/Utils/SceneCodeUtils';

export type PdfExportSortField = 'sceneCode' | 'sceneSet' | 'sceneLocation' | 'scenePeriod';
export type PdfExportSortOrder = 'asc' | 'desc';

export interface PdfScenesExportSort {
  field: PdfExportSortField;
  order: PdfExportSortOrder;
}

export const DEFAULT_PDF_SCENES_EXPORT_SORT: PdfScenesExportSort = {
  field: 'sceneCode',
  order: 'asc'
};

const PENDING_LOCATION_LABEL = '拍摄地点待定';

function applyOrder(cmp: number, order: PdfExportSortOrder): number {
  return order === 'desc' ? -cmp : cmp;
}

export function compareScenesForPdfExport(
  a: SceneWithUserNames,
  b: SceneWithUserNames,
  field: PdfExportSortField,
  order: PdfExportSortOrder,
  parseCache: Map<string, SceneCodeParts | null>
): number {
  let cmp = 0;
  switch (field) {
    case 'sceneCode':
      cmp = compareSceneCodes(a, b, parseCache);
      if (cmp === 0) cmp = (a.scenePlotDay || '').localeCompare(b.scenePlotDay || '');
      break;
    case 'sceneSet': {
      const sA = (a.sceneSet || '').trim();
      const sB = (b.sceneSet || '').trim();
      if (!sA && !sB) cmp = compareSceneCodes(a, b, parseCache);
      else if (!sA) cmp = 1;
      else if (!sB) cmp = -1;
      else cmp = sA.localeCompare(sB, 'zh-CN', { numeric: true, sensitivity: 'base' });
      if (cmp === 0) cmp = compareSceneCodes(a, b, parseCache);
      break;
    }
    case 'sceneLocation': {
      const adA = (a.sceneLocation || '').trim();
      const adB = (b.sceneLocation || '').trim();
      if (!adA && !adB) cmp = compareSceneCodes(a, b, parseCache);
      else if (!adA) cmp = 1;
      else if (!adB) cmp = -1;
      else cmp = adA.localeCompare(adB, 'zh-CN', { numeric: true, sensitivity: 'base' });
      if (cmp === 0) cmp = compareSceneCodes(a, b, parseCache);
      break;
    }
    case 'scenePeriod': {
      const pA = (a.scenePeriod || '').trim();
      const pB = (b.scenePeriod || '').trim();
      if (!pA && !pB) cmp = compareSceneCodes(a, b, parseCache);
      else if (!pA) cmp = 1;
      else if (!pB) cmp = -1;
      else cmp = pA.localeCompare(pB, 'zh-CN', { numeric: true, sensitivity: 'base' });
      if (cmp === 0) cmp = compareSceneCodes(a, b, parseCache);
      break;
    }
    default:
      cmp = 0;
  }
  return applyOrder(cmp, order);
}

export function sortScenesForPdfExport(
  scenes: SceneWithUserNames[],
  sort: PdfScenesExportSort,
  parseCache: Map<string, SceneCodeParts | null>
): SceneWithUserNames[] {
  return [...scenes].sort((a, b) => compareScenesForPdfExport(a, b, sort.field, sort.order, parseCache));
}

/** 分景表：组内排序后，再按各组首条（代表行）决定分组先后顺序 */
export function sortSetsGroupedLocations(
  scenesByLocation: Map<string, SceneWithUserNames[]>,
  sort: PdfScenesExportSort,
  parseCache: Map<string, SceneCodeParts | null>
): string[] {
  scenesByLocation.forEach((group) => {
    group.sort((a, b) => compareScenesForPdfExport(a, b, sort.field, sort.order, parseCache));
  });
  const keys = Array.from(scenesByLocation.keys());
  keys.sort((ka, kb) => {
    const groupA = scenesByLocation.get(ka)!;
    const groupB = scenesByLocation.get(kb)!;
    const repA = groupA[0];
    const repB = groupB[0];
    let cmp = compareScenesForPdfExport(repA, repB, sort.field, sort.order, parseCache);
    if (cmp !== 0) return cmp;
    const pendingA = ka === PENDING_LOCATION_LABEL ? 1 : 0;
    const pendingB = kb === PENDING_LOCATION_LABEL ? 1 : 0;
    if (pendingA !== pendingB) return pendingA - pendingB;
    return ka.localeCompare(kb, 'zh-CN', { numeric: true, sensitivity: 'base' });
  });
  return keys;
}
